/**
 * DS-Survey Module
 * Survey creation, management, and response collection system
 */

import React, { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import type { Module } from '@/modules/index';
import Layout from '@/components/Layout';
import PrivateRoute from '@/components/PrivateRoute';
import { PageSkeleton } from '@/components/base/Skeleton';

// Lazy load pages for code splitting
const SurveyDashboard = lazy(() => import('./pages/Dashboard/SurveyDashboard'));
const SurveyBuilderPage = lazy(() => import('./pages/SurveyBuilder/SurveyBuilderPage'));

// User Survey Pages
const UserSurveyListPage = lazy(() => import('./pages/UserSurveys/UserSurveyListPage'));
const MyResponsesPage = lazy(() => import('./pages/UserSurveys/MyResponsesPage'));

// Validation Pages
const ValidationDashboard = lazy(() => import('./pages/Validation/ValidationDashboard'));
const SurveyDataPage = lazy(() => import('./pages/Data/SurveyDataPage'));

// Analytics Pages
const OverallAnalyticsDashboard = lazy(() => import('./pages/Analytics/OverallAnalyticsDashboard'));
const SingleSurveyAnalytics = lazy(() => import('./pages/Analytics/SingleSurveyAnalytics'));
const SurveyReportDashboard = lazy(() => import('./pages/Analytics/SurveyReportDashboard'));
const DashboardDesigner = lazy(() => import('./pages/Analytics/DashboardDesigner'));
const DashboardDesignMaster = lazy(() => import('./pages/Analytics/DashboardDesignMaster'));
const CopySurveyPage = lazy(() => import('./pages/CopySurvey/CopySurveyPage'));

// Surveyor Pages
const SurveyorDashboardPage = lazy(() => import('./pages/Surveyor/SurveyorDashboardPage'));

// How It Works — survey lifecycle overview
const HowItWorksPage = lazy(() => import('./pages/HowItWorks/HowItWorksPage'));

// Loading fallback
const LoadingFallback = () => (
  <PageSkeleton />
);

const dsSurveyModule: Module = {
  name: 'ds-survey',
  path: '/app/ds-survey',
  routes: [
    {
      path: '/app/ds-survey',
      element: (
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SurveyDashboard />
            </Suspense>
          )
        },
        // Copy Survey to Site (static path - must come before :id catch-all)
        {
          path: 'copy-to-site',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CopySurveyPage />
            </Suspense>
          )
        },
        // Dashboard Design Master (static path - must come before :id catch-all)
        {
          path: 'design',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <DashboardDesignMaster />
            </Suspense>
          )
        },
        // How It Works (static path - must come before :id catch-all)
        {
          path: 'how-it-works',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <HowItWorksPage />
            </Suspense>
          )
        },
        {
          path: ':id',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SurveyBuilderPage />
            </Suspense>
          )
        },
        {
          path: ':id/builder',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SurveyBuilderPage />
            </Suspense>
          )
        },
        {
          path: ':id/preview',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SurveyBuilderPage />
            </Suspense>
          )
        },
        // User Survey Routes
        {
          path: 'user',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <UserSurveyListPage />
            </Suspense>
          )
        },
        {
          // "My Responses" has no standalone (all-surveys) view — MyResponsesPage
          // requires a :surveyId. Point the sidebar item / header button at the
          // User Surveys list, where each survey exposes a "View Responses" action.
          path: 'user/my-responses',
          element: <Navigate to="/app/ds-survey/user" replace />
        },
        {
          path: 'user/responses/:surveyId',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <MyResponsesPage />
            </Suspense>
          )
        },
        // Validation Routes
        {
          path: 'validations',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ValidationDashboard />
            </Suspense>
          )
        },
        // Surveyor Dashboard
        {
          path: 'surveyor',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SurveyorDashboardPage />
            </Suspense>
          )
        },
        // Analytics Routes (MUST be before :id catch-all)
        {
          path: 'analytics',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <OverallAnalyticsDashboard />
            </Suspense>
          )
        },
        {
          path: ':surveyId/analytics',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SingleSurveyAnalytics />
            </Suspense>
          )
        },
        // DTCP drill-down: State → UDA → responses
        {
          path: ':surveyId/analytics/uda/:udaId',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SingleSurveyAnalytics />
            </Suspense>
          )
        },
        // Report Dashboard Routes (more specific first!)
        {
          path: ':surveyId/reports/designer',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <DashboardDesigner />
            </Suspense>
          )
        },
        {
          path: ':surveyId/reports',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SurveyReportDashboard />
            </Suspense>
          )
        },
        // Survey Data Page
        {
          path: ':surveyId/data',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SurveyDataPage />
            </Suspense>
          )
        }
      ]
    }
  ]
};

export default dsSurveyModule;

// Re-export types and services for external use
export * from './types/survey.types';
export * from './services/survey-api.service';
export { useSurveyStore } from './store/surveyStore';

// Schedule + bitmap engine — re-exported for cross-app reuse (e.g. ds-iec)
export { default as SurveySchedule } from './components/SurveySchedule';
export {
  getDefaultScheduleBitmaps,
  isScheduledDate,
  getScheduleSummary,
} from './utils/scheduleBitmap';
export type { ScheduleBitmapConfig, ScheduleBitmaps } from './utils/scheduleBitmap';
